import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, ArrowRight, KeyRound } from 'lucide-react';
import { useApp } from '@/store/appContext';
import KompaLogo from '@/modules/shared/KompaLogo';
import ForgotPasswordModal from '@/modules/auth/ForgotPasswordModal';

// ── Stuck-user heuristic ─────────────────────────────────────────────
// We track credential failures per normalized email in localStorage so
// the recovery prompt survives reloads (a user who refreshes after 3
// failed attempts still gets the helping hand). The store is per-email
// so switching accounts won't surprise a fresh user with the banner.
const ATTEMPTS_KEY = 'kompa.loginAttempts.v1';
const STUCK_THRESHOLD = 3;
const RESET_AFTER_MS = 30 * 60 * 1000; // 30 minutes of inactivity → clear

function normEmail(e) {
  return (e || '').trim().toLowerCase();
}

function readAttempts() {
  try {
    const raw = localStorage.getItem(ATTEMPTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAttempts(map) {
  try {
    localStorage.setItem(ATTEMPTS_KEY, JSON.stringify(map));
  } catch { /* quota / private mode — ignore */ }
}

function getAttemptCount(email) {
  const map = readAttempts();
  const entry = map[normEmail(email)];
  if (!entry) return 0;
  if (Date.now() - (entry.ts || 0) > RESET_AFTER_MS) return 0;
  return entry.count || 0;
}

function bumpAttempt(email) {
  const key = normEmail(email);
  if (!key) return 0;
  const map = readAttempts();
  const prev = map[key];
  const stale = !prev || Date.now() - (prev.ts || 0) > RESET_AFTER_MS;
  const next = stale ? 1 : (prev.count || 0) + 1;
  map[key] = { count: next, ts: Date.now() };
  writeAttempts(map);
  return next;
}

function clearAttempt(email) {
  const key = normEmail(email);
  if (!key) return;
  const map = readAttempts();
  if (map[key]) {
    delete map[key];
    writeAttempts(map);
  }
}

export default function LoginPage({ onSwitchToSignup }) {
  const { login, authError, setAuthError } = useApp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);

  // Refresh attempt count whenever the typed email changes so the banner
  // shows up only for the email that's actually struggling.
  useEffect(() => {
    setAttemptCount(getAttemptCount(email));
  }, [email]);

  const showStuckBanner = useMemo(
    () => attemptCount >= STUCK_THRESHOLD && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
    [attemptCount, email],
  );

  const validate = () => {
    const newErrors = {};
    if (!email.trim()) newErrors.email = 'Correo requerido';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Correo inválido';
    if (!password.trim()) newErrors.password = 'Contraseña requerida';
    else if (password.length < 6) newErrors.password = 'Mínimo 6 caracteres';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setAuthError(null);
    const result = await login(email, password);
    setSubmitting(false);

    if (result?.ok) {
      // Successful sign-in — clear the stuck-user state for this email.
      clearAttempt(email);
      setAttemptCount(0);
    } else if (result?.reason === 'credentials') {
      // Only count REAL credential rejections — never network/5xx (those
      // aren't the user's fault and shouldn't push the recovery prompt).
      const next = bumpAttempt(email);
      setAttemptCount(next);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white px-6 md:px-8 pt-16 md:pt-20 pb-8 md:pb-10 max-w-lg mx-auto w-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex-1 flex flex-col"
      >
        <div className="flex justify-center mb-14">
          <KompaLogo size="lg" />
        </div>

        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-black tracking-tight" data-testid="login-title">
            INICIA SESIÓN
          </h1>
          <p className="text-gray-500 font-light mt-1">
            Accede a tu cuenta para continuar
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 flex-1" data-testid="login-form">
          <div>
            <div className={`flex items-center gap-3 bg-gray-50 rounded-xl px-4 h-14 transition-all border-b-2 ${errors.email ? 'border-red-400' : 'border-transparent focus-within:border-[#fbb040]'} kompa-input`}>
              <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <input
                type="email"
                placeholder="Correo electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent outline-none text-base font-medium placeholder:text-gray-400"
                data-testid="login-email-input"
              />
            </div>
            {errors.email && <p className="text-red-500 text-xs mt-1 ml-1">{errors.email}</p>}
          </div>

          <div>
            <div className={`flex items-center gap-3 bg-gray-50 rounded-xl px-4 h-14 transition-all border-b-2 ${errors.password ? 'border-red-400' : 'border-transparent focus-within:border-[#fbb040]'} kompa-input`}>
              <Lock className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex-1 bg-transparent outline-none text-base font-medium placeholder:text-gray-400"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="login-toggle-password"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.password && <p className="text-red-500 text-xs mt-1 ml-1">{errors.password}</p>}
          </div>

          <div className="text-right">
            <button
              type="button"
              onClick={() => setShowForgot(true)}
              className="text-sm font-medium text-[#1f2d5a] hover:underline"
              data-testid="forgot-password-link"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          {/* Stuck-user recovery banner — appears after 3 credential
              failures for the same email. Subtle amber tone, never red,
              so it reads as "we're here to help" not "you're locked out". */}
          <AnimatePresence>
            {showStuckBanner && (
              <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                transition={{ duration: 0.22 }}
                className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3"
                data-testid="stuck-user-banner"
              >
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <KeyRound className="w-5 h-5 text-amber-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-900 leading-tight">
                    ¿Olvidaste tu contraseña?
                  </p>
                  <p className="text-xs text-amber-800 font-light mt-0.5 leading-snug">
                    Te ayudamos a recuperarla en segundos. Te enviaremos un enlace a tu correo.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-amber-900 hover:text-amber-950 underline underline-offset-2"
                    data-testid="stuck-user-recover-cta"
                  >
                    Restablecer contraseña
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            type="submit"
            whileTap={{ scale: 0.97 }}
            disabled={submitting}
            className={`w-full bg-[#fbb040] text-black font-bold rounded-full h-14 flex items-center justify-center gap-2 hover:bg-[#ffa000] transition-colors text-lg ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
            data-testid="login-submit-button"
          >
            {submitting ? 'Ingresando...' : 'Iniciar sesión'}
            {!submitting && <ArrowRight className="w-5 h-5" />}
          </motion.button>
          {authError && <p className="text-red-500 text-sm text-center mt-3" data-testid="login-error">{authError}</p>}
        </form>

        <div className="mt-8 mb-16 text-center relative z-50">
          <p className="text-gray-500 font-light">
            ¿No tienes cuenta?{' '}
            <button
              onClick={onSwitchToSignup}
              className="font-bold text-[#1f2d5a] hover:underline py-2 px-1"
              data-testid="switch-to-signup"
            >
              Regístrate
            </button>
          </p>
        </div>
      </motion.div>

      <ForgotPasswordModal
        isOpen={showForgot}
        onClose={() => setShowForgot(false)}
        initialEmail={email}
      />
    </div>
  );
}
